// =====================================================
// 📁 QuotationProductCategories.tsx
// =====================================================

import { useEffect, useRef, useState } from 'react';

import useProductCatalog from '../../../../../../hooks/productpanel/useProductCatalog';

import { useQuotation } from '../../../../../../hooks/sale/useQuotation';

import POSCategoryScrollButton from '../../../../pos/categories/POSCategoryScrollButton';

import POSCategoryList from '../../../../pos/categories/POSCategoryList';

import POSCategoryMoreButton from '../../../../pos/categories/POSCategoryMoreButton';

import POSCategoryClearButton from '../../../../pos/categories/POSCategoryClearButton';

import CategoryModal from '../../../../ProductBrowser/Category/CategoryModal';

const QuotationProductCategories = () => {
  // =====================================================
  // 🔹 State
  // =====================================================

  const [showModal, setShowModal] = useState(false);

  // =====================================================
  // 🔹 Refs
  // =====================================================

  const scrollRef = useRef<HTMLDivElement>(null);

  // =====================================================
  // 🔹 Quotation
  // =====================================================

  const { activeBranchId } = useQuotation();

  // =====================================================
  // 🔹 Quotation State
  // =====================================================

  const branchId = activeBranchId;

  const isQuotationReady = !!branchId;

  // =====================================================
  // 🔹 Product Catalog
  // =====================================================

  const {
    categories,
    fetchCategories,
    fetchFilteredProducts,
    selectedCategoryId,
    setSelectedCategory,
    pageSize,
  } = useProductCatalog(branchId);

  // =====================================================
  // 🚀 Init
  // =====================================================

  useEffect(() => {
    if (!branchId) {
      return;
    }

    fetchCategories();
  }, [branchId]);

  // =====================================================
  // 🔹 Handlers
  // =====================================================

  const handleCategoryClick = (categoryId: number) => {
    if (!branchId) {
      return;
    }

    setSelectedCategory(categoryId);

    fetchFilteredProducts(
      {
        categoryId,
        branchId,
      },
      0,
      pageSize
    );
  };

  const handleClearSelection = () => {
    if (!branchId) {
      return;
    }

    setSelectedCategory(null);

    fetchFilteredProducts(
      {
        branchId,
      },
      0,
      pageSize
    );
  };

  const handleScroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) {
      return;
    }

    scrollRef.current.scrollBy({
      left: direction === 'left' ? -180 : 180,

      behavior: 'smooth',
    });
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <>
      <div
        className="
        flex
        items-center
        gap-2
      "
      >
        {/* ===================================================== */}
        {/* 🔹 Scroll Area */}
        {/* ===================================================== */}

        <div
          className="
          flex
          min-w-0
          flex-1
          items-center
          gap-2
        "
        >
          <POSCategoryScrollButton
            direction="left"
            disabled={!isQuotationReady}
            onClick={() => handleScroll('left')}
          />

          <POSCategoryList
            scrollRef={scrollRef}
            categories={categories}
            selectedCategoryId={selectedCategoryId}
            isSaleInitialized={isQuotationReady}
            onCategoryClick={handleCategoryClick}
          />

          <POSCategoryScrollButton
            direction="right"
            disabled={!isQuotationReady}
            onClick={() => handleScroll('right')}
          />
        </div>

        {/* ===================================================== */}
        {/* 🔹 Actions */}
        {/* ===================================================== */}

        <div
          className="
          flex
          shrink-0
          items-center
          gap-2
        "
        >
          <POSCategoryMoreButton
            disabled={!isQuotationReady}
            onClick={() => setShowModal(true)}
          />

          {selectedCategoryId && (
            <POSCategoryClearButton onClick={handleClearSelection} />
          )}
        </div>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Modal */}
      {/* ===================================================== */}

      <CategoryModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
};

export default QuotationProductCategories;
